import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css'; // Haritanın stillerinin doğru yüklenmesi için bu import önemlidir.
import RouteVoice from "../components/RouteVoice"


delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// --- API Adresi ---
const URL = process.env.REACT_APP_API_URL;
const API_BASE_URL = URL + "/api";

// --- Revize Edilmiş Modal Bileşeni ---
// Z-index değeri z-50'den daha yüksek bir değere ayarlandı.
const Modal = ({ show, title, message, onConfirm, onCancel, showConfirmButton, showCancelButton, type }) => {
    if (!show) {
        return null;
    }

    // Modal tipine göre ikon ve renk paleti belirleme
    const getModalTheme = () => {
        switch (type) {
            case 'success':
                return { icon: "fas fa-check-circle text-green-500", titleColor: "text-green-400", borderColor: "border-green-700" };
            case 'error':
                return { icon: "fas fa-exclamation-circle text-red-500", titleColor: "text-red-400", borderColor: "border-red-700" };
            case 'warning':
                return { icon: "fas fa-exclamation-triangle text-yellow-500", titleColor: "text-yellow-400", borderColor: "border-yellow-700" };
            case 'info':
            default:
                return { icon: "fas fa-info-circle text-blue-500", titleColor: "text-blue-400", borderColor: "border-blue-700" };
        }
    };

    const theme = getModalTheme();

    return (
        // Z-index'i z-[1000]'e yükseltildi. Bu değer, neredeyse tüm diğer bileşenlerin üzerinde görünmesini garantiler.
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-[1000] p-4 animate-fade-in">
            <div className={`bg-gray-800 rounded-2xl shadow-3xl border ${theme.borderColor} w-full max-w-md p-8 transform transition-all duration-300 scale-95 md:scale-100`}>

                <div className="flex flex-col items-center text-center">
                    <i className={`${theme.icon} text-6xl mb-4 animate-bounce-in`}></i>
                    <h3 className={`text-3xl font-extrabold ${theme.titleColor} mb-2`}>{title}</h3>
                    <p className="text-gray-300 mb-6">{message}</p>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                    {showCancelButton && (
                        <button
                            onClick={onCancel}
                            className="flex-1 px-6 py-3 bg-gray-600 text-white font-semibold rounded-xl shadow-lg hover:bg-gray-700 transform hover:-translate-y-1 transition-all duration-300"
                        >
                            İptal
                        </button>
                    )}
                    {showConfirmButton && (
                        <button
                            onClick={onConfirm}
                            className="flex-1 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:bg-indigo-700 transform hover:-translate-y-1 transition-all duration-300"
                        >
                            Onayla
                        </button>
                    )}
                    {!showConfirmButton && !showCancelButton && (
                        <button
                            onClick={onCancel}
                            className="flex-1 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:bg-indigo-700 transform hover:-translate-y-1 transition-all duration-300"
                        >
                            Kapat
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Rota Detay Sayfası Bileşeni ---
const RotaDetaySayfasi = () => {
    const { rotaId } = useParams();
    const navigate = useNavigate();

    const [rota, setRota] = useState(null);
    const [yukleniyor, setYukleniyor] = useState(true);
    const [hata, setHata] = useState(null);
    const [baslatilmisRota, setBaslatilmisRota] = useState(null);
    const [completedMekanIndices, setCompletedMekanIndices] = useState([]);
    const [tumMekanlar, setTumMekanlar] = useState([]);
    // Modal state'leri
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState({
        title: '',
        message: '',
        onConfirm: () => { },
        onCancel: () => setShowModal(false),
        showCancelButton: false,
        showConfirmButton: false,
        type: 'info',
    });
    // Component yüklendiğinde mekanlar.json dosyasını çekiyoruz.
    useEffect(() => {
        // public klasöründeki dosyaya doğrudan erişim
        fetch('/ankaraMekanlar.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => setTumMekanlar(data))
            .catch(error => console.error("Mekanların JSON verisi yüklenemedi:", error));
    }, []); // Sadece component ilk yüklendiğinde çalışır

    const googleMapsUrl = useMemo(() => {
        if (!rota?.mekanDetaylari_iliskisi || rota.mekanDetaylari_iliskisi.length === 0) {
            return "#"; // Eğer mekan yoksa boş link
        }

        // Mekanların koordinatlarını 'latitude,longitude' formatında birleştir
        const waypoints = rota.mekanDetaylari_iliskisi
            .map(mekan => `${mekan.latitude},${mekan.longitude}`)
            .join('/');

        // Google Maps'in 'directions' URL formatını kullan
        return `https://www.google.com/maps/dir/${waypoints}`;

    }, [rota]); // Sadece 'rota' değiştiğinde bu URL'i yeniden hesapla

    const openModal = (title, message, type = 'info', onConfirm = null, showConfirm = false, showCancel = false) => {
        setModalContent({
            title,
            message,
            onConfirm: onConfirm || (() => setShowModal(false)),
            onCancel: () => setShowModal(false),
            showConfirmButton: showConfirm,
            showCancelButton: showCancel,
            type,
        });
        setShowModal(true);
    };

    const getUserIdFromToken = () => {
        const token = localStorage.getItem('token');
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.user_id;
        } catch (e) {
            console.error("Token çözümlenemedi:", e);
            return null;
        }
    };
    const currentUserId = getUserIdFromToken();

    const fetchRotaVeBaslatilmisDurum = useCallback(async () => {
        try {
            setYukleniyor(true);
            setHata(null);

            const rotaResponse = await axios.get(`${API_BASE_URL}/rotalar/${rotaId}`);
            setRota(rotaResponse.data);

            if (currentUserId) {
                const startedRotasResponse = await axios.get(`${API_BASE_URL}/users/${currentUserId}/started-rotas`);
                const foundStartedRota = startedRotasResponse.data.find(sr => sr.rota_id === rotaId);

                if (foundStartedRota) {
                    setBaslatilmisRota(foundStartedRota);
                    setCompletedMekanIndices(foundStartedRota.completed_mekan_indices || []);
                } else {
                    setBaslatilmisRota(null);
                    setCompletedMekanIndices([]);
                }
            }
        } catch (err) {
            console.error('API Hatası:', err);
            if (err.response && err.response.status === 404) {
                setHata('Aradığınız rota bulunamadı.');
            } else {
                setHata('Rota detayları veya başlatılmış rota durumu yüklenirken bir hata oluştu: ' + (err.response?.data?.detail || err.message));
            }
        } finally {
            setYukleniyor(false);
        }
    }, [rotaId, currentUserId]);

    useEffect(() => {
        if (rotaId) {
            fetchRotaVeBaslatilmisDurum();
        } else {
            setHata("Rota ID bulunamadı.");
            setYukleniyor(false);
        }
    }, [rotaId, fetchRotaVeBaslatilmisDurum]);

    // Rotayı başlatmadan önce onay modalını açan fonksiyon
    const handleRotayiBaslat = () => {
        if (!currentUserId) {
            openModal("Giriş Gerekli", "Rotayı başlatmak için lütfen giriş yapın.", 'warning', () => navigate('/login'), true, true);
            return;
        }
        openModal(
            "Rotayı Başlat",
            "Bu rotayı başlatmak istediğinizden emin misiniz?",
            'info',
            () => handleConfirmRotayiBaslat(),
            true,
            true
        );
    };

    // Onaylandıktan sonra rotayı başlatan asıl fonksiyon
    const handleConfirmRotayiBaslat = async () => {
        setShowModal(false); // Modal'ı kapat

        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error("Token bulunamadı.");

            const response = await axios.post(
                `${API_BASE_URL}/users/${currentUserId}/started-rotas`,
                { rota_id: rotaId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setBaslatilmisRota(response.data);
            setCompletedMekanIndices(response.data.completed_mekan_indices || []);
            openModal("Başarılı", "Rota başarıyla başlatıldı!", 'success');
        } catch (err) {
            console.error("Rotayı başlatırken hata:", err.response?.data || err.message);
            const errorMessage = "Rotayı başlatırken bir hata oluştu: " + (err.response?.data?.detail || err.message);
            openModal("Hata", errorMessage, 'error');
            if (err.response?.status === 401) navigate('/login');
        }
    };

    const handleMekanTamamla = async (mekanIndex) => {
        if (!currentUserId || !baslatilmisRota || baslatilmisRota.is_completed) {
            if (!currentUserId) openModal("Uyarı", "Mekan işaretlemek için rotanın başlatılmış olması gerekiyor.", 'warning');
            else if (baslatilmisRota.is_completed) openModal("Uyarı", "Rota zaten tamamlanmış, mekanları değiştiremezsiniz.", 'warning');
            return;
        }

        const newCompletedMekanIndices = completedMekanIndices.includes(mekanIndex)
            ? completedMekanIndices.filter(index => index !== mekanIndex)
            : [...completedMekanIndices, mekanIndex];

        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error("Token bulunamadı.");

            const response = await axios.put(
                `${API_BASE_URL}/users/${currentUserId}/started-rotas/${baslatilmisRota.id}`,
                { completed_mekan_indices: newCompletedMekanIndices },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setBaslatilmisRota(response.data);
            setCompletedMekanIndices(response.data.completed_mekan_indices || []);
        } catch (err) {
            console.error("Mekan tamamlama hatası:", err.response?.data || err.message);
            const errorMessage = "Mekan işaretlenirken bir hata oluştu: " + (err.response?.data?.detail || err.message);
            openModal("Hata", errorMessage, 'error');
            if (err.response?.status === 401) navigate('/login');
        }
    };

    // REVİZE EDİLDİ: "Detayları Gör" butonuna tıklanınca çalışacak fonksiyon
    const handleDetayClick = (mekan) => {
        const mekanAdi = mekan.ad;

        if (!mekanAdi) {
            console.error("Mekan adı bulunamadı.", mekan);
            openModal("Hata", "Mekan bilgileri eksik, detaylar görüntülenemiyor.", 'error');
            return;
        }

        // `tumMekanlar` (ankaraMekanlar.json'dan gelen veri) içinde bu isme sahip mekanı bul.
        // İsimlerin tam olarak eşleştiğini varsayıyoruz.
        const bulunanMekan = tumMekanlar.find(m => m.ad === mekanAdi);

        if (bulunanMekan && bulunanMekan.id) {
            // Mekan JSON'da bulundu ve bir 'id'si var. Detay sayfasına yönlendir.
            const mekanId = bulunanMekan.id;
            console.log(`'${mekanAdi}' (ID: ${mekanId}) JSON'da bulundu. /mekan/${mekanId} adresine yönlendiriliyor...`);
            navigate(`/mekan/${mekanId}`);
        } else {
            // Mekan JSON'da bulunamadı veya 'id' alanı yok. Kullanıcıyı bilgilendir.
            console.log(`'${mekanAdi}' JSON dosyasında bulunamadı veya geçerli bir ID'ye sahip değil.`);
            openModal("Bilgi", "Bu mekanın detay sayfası henüz mevcut değildir.", 'info');
        }
    };


    // Rotayı bitirmeden önce onay modalını açan fonksiyon
    const handleRotayiBitir = () => {
        if (!currentUserId || !baslatilmisRota) return;

        const toplamMekanSayisi = rota.mekanDetaylari_iliskisi ? rota.mekanDetaylari_iliskisi.length : 0;
        const tamamlananMekanSayisi = completedMekanIndices.length;

        if (tamamlananMekanSayisi !== toplamMekanSayisi) {
            openModal(
                "Uyarı",
                "Rotadaki tüm mekanları işaretlemediniz. Yine de rotayı bitirmek istediğinizden emin misiniz?",
                'warning',
                () => handleConfirmRotayiBitir(),
                true,
                true
            );
            return;
        }

        openModal(
            "Rotayı Bitir",
            "Rotayı tamamladığınızdan emin misiniz?",
            'info',
            () => handleConfirmRotayiBitir(),
            true,
            true
        );
    };

    // Onaylandıktan sonra rotayı bitiren asıl fonksiyon
    const handleConfirmRotayiBitir = async () => {
        setShowModal(false);

        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error("Token bulunamadı.");

            const response = await axios.put(
                `${API_BASE_URL}/users/${currentUserId}/started-rotas/${baslatilmisRota.id}`,
                { is_completed: true },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setBaslatilmisRota(response.data);
            openModal("Başarılı", "Rota başarıyla tamamlandı!", 'success');
        } catch (err) {
            console.error("Rotayı tamamlarken hata:", err.response?.data || err.message);
            const errorMessage = "Rotayı tamamlarken bir hata oluştu: " + (err.response?.data?.detail || err.message);
            openModal("Hata", errorMessage, 'error');
            if (err.response?.status === 401) navigate('/login');
        }
    };

    if (yukleniyor) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white p-4">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-teal-500 border-dotted rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-xl font-semibold text-gray-400">Rota detayları yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (hata) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4">
                <div className="text-center bg-gray-800 p-8 rounded-xl shadow-xl border border-gray-700">
                    <i className="fas fa-exclamation-triangle text-red-500 text-6xl mb-4"></i>
                    <p className="text-xl font-semibold mb-6 text-red-400">{hata}</p>
                    <button onClick={() => navigate('/route')} className="bg-indigo-600 text-white font-semibold py-2 px-6 rounded-lg shadow-lg hover:bg-indigo-700 transition-colors duration-200">
                        Rotalara Geri Dön
                    </button>
                </div>
            </div>
        );
    }

    if (!rota) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4">
                <div className="text-center bg-gray-800 p-8 rounded-xl shadow-xl border border-gray-700">
                    <h1 className="text-3xl font-bold text-yellow-400 mb-2">Rota Bulunamadı</h1>
                    <p className="text-lg text-gray-400 mb-6">Üzgünüz, aradığınız rota bulunamadı veya silinmiş olabilir.</p>
                    <button onClick={() => navigate('/route')} className="bg-indigo-600 text-white font-semibold py-2 px-6 rounded-lg shadow-lg hover:bg-indigo-700 transition-colors duration-200">
                        Rotalara Geri Dön
                    </button>
                </div>
            </div>
        );
    }

    const isRotaBaslatilmis = !!baslatilmisRota;
    const isRotaTamamlanmis = baslatilmisRota?.is_completed;
    const toplamMekanSayisi = rota.mekanDetaylari_iliskisi ? rota.mekanDetaylari_iliskisi.length : 0;
    const tamamlananMekanSayisi = completedMekanIndices.length;

    const defaultCenter = [41.0082, 28.9784];
    let mapCenter = defaultCenter;
    let mapZoom = 13;
    let polylinePositions = [];

    if (rota.mekanDetaylari_iliskisi && rota.mekanDetaylari_iliskisi.length > 0) {
        const validMekanlar = rota.mekanDetaylari_iliskisi.filter(mekan => {
            const lat = parseFloat(mekan.latitude);
            const lon = parseFloat(mekan.longitude);
            return !isNaN(lat) && !isNaN(lon);
        });

        if (validMekanlar.length > 0) {
            const firstMekan = validMekanlar[0];
            mapCenter = [parseFloat(firstMekan.latitude), parseFloat(firstMekan.longitude)];
            mapZoom = 14;
            polylinePositions = validMekanlar.map(mekan => [parseFloat(mekan.latitude), parseFloat(mekan.longitude)]);
        }
    }

    return (
        <>
            <Modal
                show={showModal}
                title={modalContent.title}
                message={modalContent.message}
                onConfirm={modalContent.onConfirm}
                onCancel={modalContent.onCancel}
                showConfirmButton={modalContent.showConfirmButton}
                showCancelButton={modalContent.showCancelButton}
                type={modalContent.type}
            />
            <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-6 lg:p-8">
                {/* Sayfa Üst Bölümü */}
                <div className="max-w-7xl mx-auto mb-8">
                    <button onClick={() => navigate('/route')} className="flex items-center space-x-2 text-indigo-400 hover:text-indigo-300 transition-colors duration-200 mb-6">
                        <i className="fas fa-arrow-left"></i>
                        <span>Rotalara Geri Dön</span>
                    </button>
                    <h1 className="text-4xl font-extrabold text-white mb-2">{rota.baslik}</h1>
                    <p className="text-lg text-gray-400">{rota.il}, {rota.ilce} &bull; Ortalama Süre: {rota.ortalamaSure}</p>
                </div>

                {/* Ana İçerik Gridi */}
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Sol ve Orta Kolon (Ana İçerik) */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Genel Bilgi ve Görsel */}
                        <div className="bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 border border-gray-700">
                            <div className="space-y-6">
                                {rota.gorsel && (
                                    <img src={rota.gorsel} alt={rota.baslik} className="w-full h-80 object-cover rounded-lg shadow-md" />
                                )}
                                <RouteVoice
                                    routeId={rotaId}
                                    title={rota.aciklama}
                                />
                                <p className="text-gray-300 leading-relaxed mt-4">{rota.aciklama}</p>
                            </div>
                        </div>

                        {/* Rotadaki Mekanlar ve Harita */}
                        <div className="bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 border border-gray-700">
                            <h2 className="text-2xl font-bold text-teal-400 mb-6">Rotadaki Mekanlar</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Mekan Listesi */}
                                <div>
                                    <ul className="space-y-4">
                                        {rota.mekanDetaylari_iliskisi && rota.mekanDetaylari_iliskisi.length > 0 ? (
                                            rota.mekanDetaylari_iliskisi.map((mekan, index) => (
                                                <li
                                                    key={index}
                                                    className={`p-4 rounded-lg transition-colors duration-200 shadow-md ${completedMekanIndices.includes(index) ? 'bg-green-900 bg-opacity-40 border border-green-700' : 'bg-gray-700 border border-gray-600'}`}
                                                >
                                                    {/* Ana Kapsayıcı */}
                                                    <div className="flex justify-between items-center w-full">

                                                        {/* Sol Taraf: Checkbox ve Metinler */}
                                                        <div className="flex items-center space-x-4">
                                                            {isRotaBaslatilmis && !isRotaTamamlanmis && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={completedMekanIndices.includes(index)}
                                                                    onChange={() => handleMekanTamamla(index)}
                                                                    id={`mekan-${mekan.id || index}`}
                                                                    className="h-5 w-5 rounded text-indigo-600 bg-gray-600 border-gray-500 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                                                                />
                                                            )}
                                                            {isRotaBaslatilmis && isRotaTamamlanmis && (
                                                                <span className="text-green-500 text-2xl flex-shrink-0">
                                                                    <i className="fas fa-check-circle"></i>
                                                                </span>
                                                            )}
                                                            <label htmlFor={`mekan-${mekan.id || index}`} className="space-y-1 cursor-pointer">
                                                                <h3 className="text-xl font-semibold text-white">{mekan.ad}</h3>
                                                                <p className="text-sm text-gray-400">{mekan.bilgi}</p>
                                                                {mekan.il && mekan.ilce && (
                                                                    <p className="text-xs text-gray-500 mt-1">Konum: {mekan.il}, {mekan.ilce}</p>
                                                                )}
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleDetayClick(mekan)}
                                                        className="mt-2 -mb-2 inline-flex items-center text-sky-400 border border-sky-500 font-medium py-1 px-4 rounded-full transition-colors duration-300 hover:bg-sky-500 hover:text-white"
                                                    >
                                                        Detayları Gör
                                                    </button>
                                                </li>
                                            ))
                                        ) : (
                                            rota.icerdiğiYapılar && rota.icerdiğiYapılar.length > 0 ? (
                                                rota.icerdiğiYapılar.map((yapi, index) => (
                                                    <li key={index} className="p-4 bg-gray-700 rounded-lg shadow-md border border-gray-600">
                                                        <h3 className="text-xl font-semibold text-white">{yapi}</h3>
                                                        <p className="text-sm text-gray-400 mt-1">Bu mekan hakkında daha fazla bilgi yakında eklenecektir.</p>
                                                    </li>
                                                ))
                                            ) : (
                                                <p className="text-gray-500 italic">Bu rota için mekan bilgisi bulunmamaktadır.</p>
                                            )
                                        )}
                                    </ul>
                                </div>
                                {/* Harita */}
                                {/* --- HARİTA VE BUTON ALANI (YENİ DÜZEN) --- */}
                                <div className="md:col-span-1 flex flex-col">
                                    <h2 className="text-2xl font-bold text-teal-400 mb-4 lg:hidden">Harita</h2>

                                    {/* Harita */}
                                    <div className="md:col-span-1">
                                        <h2 className="text-2xl font-bold text-teal-400 mb-4 lg:hidden">Harita</h2>
                                        {polylinePositions.length > 0 ? (
                                            // MapContainer'a bir key prop'u eklemek, veriler güncellendiğinde haritanın yeniden yüklenmesini sağlar.
                                            <MapContainer
                                                key={rotaId}
                                                center={mapCenter}
                                                zoom={mapZoom}
                                                scrollWheelZoom={true}
                                                className="h-[500px] w-full rounded-xl shadow-lg border-2 border-gray-700"
                                            >
                                                <TileLayer
                                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                />
                                                {rota.mekanDetaylari_iliskisi.map((mekan, index) => {
                                                    const lat = parseFloat(mekan.latitude);
                                                    const lon = parseFloat(mekan.longitude);
                                                    if (!isNaN(lat) && !isNaN(lon)) {
                                                        return (
                                                            <Marker key={index} position={[lat, lon]}>
                                                                <Popup>
                                                                    <strong>{mekan.ad}</strong><br />
                                                                    {mekan.bilgi}<br />
                                                                    {mekan.il && mekan.ilce && `Konum: ${mekan.il}, ${mekan.ilce}`}
                                                                </Popup>
                                                            </Marker>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                {polylinePositions.length > 1 && (
                                                    <Polyline
                                                        positions={polylinePositions}
                                                        color="#C29D59"
                                                        weight={5}
                                                        opacity={0.8}
                                                    />
                                                )}
                                            </MapContainer>
                                        ) : (
                                            <div className="h-[500px] bg-gray-700 rounded-xl flex items-center justify-center border-2 border-gray-700">
                                                <p className="text-gray-500 italic text-center p-4">Bu rota için harita üzerinde görüntülenebilecek mekan koordinatları bulunmamaktadır.</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* --- YENİ VE İYİLEŞTİRİLMİŞ BUTON --- */}
                                    {polylinePositions.length > 0 && (
                                        <a
                                            href={googleMapsUrl} // Dinamik olarak oluşturulan URL
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="
                      mt-4 flex items-center justify-center w-full px-4 py-3 
                      bg-blue-600 text-white font-semibold rounded-lg shadow-md 
                      hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-blue-500
                    "
                                        >
                                            Rotayı Google Haritalar'da Aç
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sağ Kolon */}
                    <div className="lg:col-span-1 space-y-8">
                        {/* Rota Aksiyonları */}
                        <div className="bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 border border-gray-700">
                            <h2 className="text-2xl font-bold text-teal-400 mb-6">Rota Durumu</h2>
                            {currentUserId ? (
                                <>
                                    {!isRotaBaslatilmis ? (
                                        <button
                                            className="w-full bg-indigo-600 text-white font-semibold py-3 px-4 rounded-lg shadow-lg hover:bg-indigo-700 transition-colors duration-200 flex items-center justify-center space-x-2"
                                            onClick={handleRotayiBaslat}
                                        >
                                            <i className="fas fa-play-circle"></i> <span>Rotayı Başlat</span>
                                        </button>
                                    ) : (
                                        <>
                                            {isRotaTamamlanmis ? (
                                                <p className="text-center text-green-400 text-xl font-bold py-2 flex items-center justify-center space-x-2">
                                                    <i className="fas fa-trophy"></i> <span>Bu rota tamamlandı!</span>
                                                </p>
                                            ) : (
                                                <button
                                                    className="w-full bg-yellow-500 text-gray-900 font-semibold py-3 px-4 rounded-lg shadow-lg hover:bg-yellow-400 transition-colors duration-200 flex items-center justify-center space-x-2"
                                                    onClick={handleRotayiBitir}
                                                >
                                                    <i className="fas fa-flag-checkered"></i> <span>Rotayı Bitir</span>
                                                </button>
                                            )}
                                            <p className="text-center text-lg text-gray-400 mt-4">Durum: <strong className={isRotaTamamlanmis ? "text-green-400" : "text-yellow-400"}>{isRotaTamamlanmis ? "Tamamlandı" : "Devam Ediyor"}</strong></p>
                                            <div className="w-full bg-gray-700 rounded-full h-3 mt-4">
                                                <div
                                                    className="bg-teal-500 h-3 rounded-full transition-all duration-500 ease-in-out"
                                                    style={{ width: `${(tamamlananMekanSayisi / toplamMekanSayisi) * 100 || 0}%` }}
                                                ></div>
                                            </div>
                                            <p className="text-center text-sm text-gray-400 mt-2">
                                                İlerleme: <strong className="text-white">{tamamlananMekanSayisi}</strong> / <strong className="text-white">{toplamMekanSayisi}</strong> Mekan Tamamlandı
                                            </p>
                                        </>
                                    )}
                                </>
                            ) : (
                                <p className="text-center text-gray-500 italic p-4 border border-gray-700 rounded-lg">
                                    Rotayı başlatmak ve ilerlemeni takip etmek için lütfen <a href="/login" className="text-indigo-400 hover:text-indigo-300 underline">giriş yap</a>.
                                </p>
                            )}
                        </div>

                        {/* Öğrenim Bilgileri */}
                        <div className="bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 border border-gray-700">
                            <h2 className="text-2xl font-bold text-teal-400 mb-6">Bu Rotadan Neler Öğreneceksiniz?</h2>
                            <ul className="space-y-3 text-gray-300">
                                {rota.nelerOgreneceksiniz && rota.nelerOgreneceksiniz.length > 0 ? (
                                    rota.nelerOgreneceksiniz.map((bilgi, index) => (
                                        <li key={index} className="flex items-start space-x-3">
                                            <i className="fas fa-book-open text-indigo-400 mt-1"></i>
                                            <span className="flex-1">{bilgi}</span>
                                        </li>
                                    ))
                                ) : (
                                    <p className="text-gray-500 italic">Bu rota için öğrenim bilgisi bulunmamaktadır.</p>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RotaDetaySayfasi;

